import React, { useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import {
  Paper,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableBody,
  Collapse,
  Box,
  Typography,
  TableCell,
  Popover,
  Button,
} from "@material-ui/core";
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
  Add,
  Edit,
  MoreVert,
  FileCopy,
} from "@material-ui/icons";
import {
  JobMeasureQuoteLineInfo,
  JobMeasureQuoteLineGroup,
  JobScheduleClaims,
  JobTagDetailInfo,
} from "../../types/JobTypes";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../components/StyledTableComponents";
import AddToScheduleDialog from "./AddToScheduleDialog";
import { AddDuplicateDetailingScheduleDialog } from "./AddDuplicateDetailingScheduleDialogProps";
import { EditDuplicateDetailingScheduleDialog } from "./EditDuplicateDetailingScheduleDialogProps";
import { API } from "aws-amplify";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      "& > *": {
        borderBottom: "unset",
      },
    },
    table: {
      minWidth: 650,
    },
    red: {
      color: "red",
    },
    orange: {
      color: "orange",
    },
    green: {
      color: "green",
    },
    popover: {
      marginTop: 8,
      pointerEvents: "none",
    },
    paper: {
      padding: theme.spacing(1),
    },
    dollarAmountCell: {
      textAlign: "right",
    },
  }),
);

interface JobMeasureTableProps {
  measureData: Array<JobMeasureQuoteLineGroup>;
  refreshData: () => void;
}

export function JobMeasureTable({
  measureData,
  refreshData,
}: JobMeasureTableProps) {
  const classes = useStyles();

  return (
    <>
      <TableContainer component={Paper} style={{ marginTop: 16 }}>
        <Table className={classes.table} size="small" aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell></TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Area</TableCell>
              <TableCell>Total Kg</TableCell>
              <TableCell className={classes.dollarAmountCell}>
                Supply Total
              </TableCell>
              <TableCell className={classes.dollarAmountCell}>
                Place Total
              </TableCell>
              <TableCell className={classes.dollarAmountCell}>Total</TableCell>
              <TableCell className={classes.dollarAmountCell}>
                Claimed
              </TableCell>
              <TableCell className={classes.dollarAmountCell}>Adj.</TableCell>
              <TableCell className={classes.dollarAmountCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {measureData.map((group: JobMeasureQuoteLineGroup, idx: number) => (
              <MeasureTableRow
                key={idx}
                group={group}
                refreshData={refreshData}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

interface MeasureTableRowProps {
  group: JobMeasureQuoteLineGroup;
  refreshData: () => void;
}

function MeasureTableRow({ group, refreshData }: MeasureTableRowProps) {
  const classes = useStyles();
  const [open, setOpen] = useState<boolean>(false);
  const [openSchedule, setOpenSchedule] = useState<boolean>(false);
  const [openDuplicateSchedule, setOpenDuplicateSchedule] =
    useState<boolean>(false);
  const [selectedJobMeasureQuoteInfo, setSelectedJobMeasureQuoteInfo] =
    useState<JobMeasureQuoteLineInfo>();
  const [openEditSchedule, setOpenEditSchedule] = useState<boolean>(false);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [anchorActionEl, setAnchorActionEl] =
    React.useState<HTMLElement | null>(null);
  const openAdjustments = Boolean(anchorEl);

  const handleAdjustmentsOpen = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleAdjustmentsClose = () => {
    setAnchorEl(null);
  };

  const getAmountTextColor = (a: number, b: number) => {
    if (a < b) {
      return classes.red;
    } else if (a === b) {
      return classes.green;
    } else {
      return classes.orange;
    }
  };

  const getAdjTextColor = (a: number) => {
    if (a < 0) {
      return classes.red;
    } else if (a > 0) {
      return classes.green;
    }
  };

  const handleClickOpen = (line: JobMeasureQuoteLineInfo) => {
    setSelectedJobMeasureQuoteInfo(line);
    setOpenSchedule(true);
  };

  const handleClose = (value: boolean) => {
    setOpenSchedule(false);
    // setOpenSubScheduleDialog(false);
    refreshData();
    // setSelectedValue(value);
  };

  const handleClickDuplicateScheduleOpen = (
    group: JobMeasureQuoteLineGroup,
  ) => {
    console.log("handleClickDuplicateScheduleOpen", group);
    setOpenDuplicateSchedule(true);
  };

  const handleDuplicateScheduleClose = async (
    fromScheduleID: string,
    newScheduleID: string,
    newScheduleName: string,
  ) => {
    if (newScheduleID && newScheduleName) {
      // Logic to duplicate the schedule
      console.log("Duplicating schedule with value:", fromScheduleID);
      console.log("New Schedule ID:", newScheduleID);
      console.log("New Schedule Name:", newScheduleName);
      const jobNo = group.lines[0].jobSeqNo;
      console.log("Job", jobNo);

      const data = {
        jobSeqNo: jobNo,
        fromScheduleNo: fromScheduleID,
        toScheduleNo: newScheduleID,
        toScheduleName: newScheduleName,
      };
      let params = {
        body: {
          ...data,
        },
      };
      //Post
      try {
        await API.post("", `/encore/jobs/${jobNo}/measure/copy`, params);
        // handleCloseDialog(false);
      } catch (error) {
        console.log(error);
        // setError("Error: Unable to post record.");
      }
    }
    setOpenDuplicateSchedule(false);
    refreshData();
  };

  const handleClickEditScheduleOpen = (group: JobMeasureQuoteLineGroup) => {
    console.log("handleClickEditScheduleOpen", group);
    setOpenEditSchedule(true);
  };
  const handleEditScheduleClose = async (
    fromScheduleID: string,
    newScheduleID: string,
    newScheduleName: string,
  ) => {
    setOpenEditSchedule(false);
    if (newScheduleName) {
      // Logic to duplicate the schedule
      console.log("Duplicating schedule with value:", fromScheduleID);
      console.log("New Schedule ID:", newScheduleID);
      console.log("New Schedule Name:", newScheduleName);
      const jobNo = group.lines[0].jobSeqNo;
      // You can add your duplication logic here
      const data = {
        jobSeqNo: jobNo,
        fromScheduleNo: fromScheduleID,
        // toScheduleNo: newScheduleID,
        toScheduleName: newScheduleName,
      };
      let params = {
        body: {
          ...data,
        },
      };
      //Post
      try {
        await API.patch(
          "",
          `/encore/jobs/${jobNo}/measure/schedule/${fromScheduleID}`,
          params,
        );
        // handleCloseDialog(false);
      } catch (error) {
        console.log(error);
        // setError("Error: Unable to post record.");
      } finally {
        refreshData();
      }
    }
  };

  const handleDeleteSchedule = (group: JobMeasureQuoteLineGroup) => {
    // Confirm deletion logic here
    if (window.confirm("Are you sure you want to delete this schedule?")) {
      // Call the delete function here
      console.log("Delete schedule:", group.schedule);
      const jobNo = group.lines[0].jobSeqNo;
      const scheduleNo = group.schedule.toString();
      console.log("Job", jobNo, "Schedule", scheduleNo);
      API.del("", `/encore/jobs/${jobNo}/measure/schedule/${scheduleNo}`, {})
        .then(() => {
          console.log("Schedule deleted successfully");
        })
        .catch((error) => {
          console.error("Error deleting schedule:", error);
        })
        .finally(() => {
          refreshData();
        });
    }
  };

  return (
    <>
      <TableRow className={classes.root}>
        <TableCell>
          <IconButton onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell component="th" scope="row">
          <strong>{group.schedule}</strong>
        </TableCell>
        <TableCell>
          <strong>{group.area}</strong>
        </TableCell>
        <TableCell>
          <strong>{Math.round(group.totalWeight)}</strong>
        </TableCell>
        <TableCell className={classes.dollarAmountCell}>
          <strong>{`$${group.supplyTotal.toFixed(2)}`}</strong>
        </TableCell>
        <TableCell className={classes.dollarAmountCell}>
          <strong>{`$${group.placeTotal.toFixed(2)}`}</strong>
        </TableCell>
        <TableCell className={classes.dollarAmountCell}>
          <strong>{`$${group.total.toFixed(2)}`}</strong>
        </TableCell>
        <TableCell
          className={`${getAmountTextColor(group.claimed, group.total)} ${
            classes.dollarAmountCell
          }`}
        >
          <strong>{`$${group.claimed.toFixed(2)}`}</strong>
        </TableCell>
        <TableCell
          className={`${getAdjTextColor(group.adjustmentTotal)} ${
            classes.dollarAmountCell
          }`}
        >
          {group.claims.length > 0 ? (
            <strong
              onMouseEnter={handleAdjustmentsOpen}
              onMouseLeave={handleAdjustmentsClose}
            >
              {`$${group.adjustmentTotal.toFixed(2)}`}
            </strong>
          ) : (
            <strong>{`$${group.adjustmentTotal.toFixed(2)}`}</strong>
          )}
        </TableCell>
        <TableCell className={classes.dollarAmountCell}>
          <React.Fragment>
            <IconButton
              aria-label="actions"
              onClick={(event) => setAnchorActionEl(event.currentTarget)}
            >
              <MoreVert />
            </IconButton>
            <Popover
              open={Boolean(anchorActionEl)}
              anchorEl={anchorActionEl}
              onClose={() => setAnchorActionEl(null)}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box display="flex" flexDirection="column" p={1}>
                <Button
                  startIcon={<FileCopy />}
                  onClick={() => {
                    setAnchorActionEl(null);
                    handleClickDuplicateScheduleOpen(group);
                  }}
                >
                  Copy
                </Button>
                <Button
                  startIcon={<Edit />}
                  onClick={() => {
                    setAnchorActionEl(null);
                    handleClickEditScheduleOpen(group);
                  }}
                >
                  Edit
                </Button>
                <Button
                  startIcon={
                    <svg width="20" height="20" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M6,19A2,2 0 0,0 8,21H16A2,2 0 0,0 18,19V7H6V19M19,4H15.5L14.79,3.29C14.42,2.9 13.88,2.9 13.5,3.29L12.79,4H9V6H19V4Z"
                      />
                    </svg>
                  }
                  onClick={() => {
                    setAnchorActionEl(null);
                    handleDeleteSchedule(group);
                    // Confirm deletion logic here
                    // if (
                    //   window.confirm(
                    //     "Are you sure you want to delete this schedule?"
                    //   )
                    // ) {
                    //   // Call the delete function here
                    //   console.log("Delete schedule:", group.schedule);
                    //   refreshData();
                    // }
                  }}
                  color="secondary"
                >
                  Delete
                </Button>
              </Box>
            </Popover>
          </React.Fragment>
        </TableCell>

        <Popover
          id="mouse-over-popover"
          className={classes.popover}
          classes={{
            paper: classes.paper,
          }}
          open={openAdjustments}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          onClose={handleAdjustmentsClose}
          disableRestoreFocus
        >
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.dollarAmountCell}>
                    Actual Amount
                  </TableCell>
                  <TableCell className={classes.dollarAmountCell}>
                    Claim Amount
                  </TableCell>
                  <TableCell className={classes.dollarAmountCell}>
                    Adjustment Total
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {group.claims.map((claim: JobScheduleClaims, idx: number) => (
                  <StyledTableRow key={idx}>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      ${claim.actualAmount}
                    </StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      ${claim.claimAmount}
                    </StyledTableCell>
                    <StyledTableCell
                      className={`${getAdjTextColor(claim.adjustmentTotal)} ${
                        classes.dollarAmountCell
                      }`}
                    >
                      ${claim.adjustmentTotal}
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Popover>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Item</StyledTableCell>
                    <StyledTableCell>Type</StyledTableCell>
                    <StyledTableCell>Product</StyledTableCell>
                    <StyledTableCell>Quantity</StyledTableCell>
                    <StyledTableCell>Claimed Kg</StyledTableCell>
                    <StyledTableCell>UOM</StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      Supply Rate
                    </StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      Place Rate
                    </StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      Supply Total
                    </StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      Place Total
                    </StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      Total
                    </StyledTableCell>
                    <StyledTableCell className={classes.dollarAmountCell}>
                      Claimed
                    </StyledTableCell>
                    <StyledTableCell></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.lines.map((line: JobMeasureQuoteLineInfo) => (
                    <StyledTableRow key={line.id}>
                      <StyledTableCell>{line.item}</StyledTableCell>
                      <StyledTableCell>{line.type}</StyledTableCell>
                      <StyledTableCell>{line.product}</StyledTableCell>
                      <StyledTableCell>{Math.round(line.kg)}</StyledTableCell>
                      <StyledTableCell
                        className={getAmountTextColor(line.claimedKg, line.kg)}
                      >
                        {Math.round(line.claimedKg)}
                      </StyledTableCell>
                      <StyledTableCell>{line.uom}</StyledTableCell>
                      <StyledTableCell
                        className={classes.dollarAmountCell}
                      >{`$${line.supplyRate.toFixed(3)}`}</StyledTableCell>
                      <StyledTableCell
                        className={classes.dollarAmountCell}
                      >{`$${line.placeRate.toFixed(3)}`}</StyledTableCell>
                      <StyledTableCell
                        className={classes.dollarAmountCell}
                      >{`$${line.supplyAmount.toFixed(2)}`}</StyledTableCell>
                      <StyledTableCell
                        className={classes.dollarAmountCell}
                      >{`$${line.placeAmount.toFixed(2)}`}</StyledTableCell>
                      <StyledTableCell
                        className={classes.dollarAmountCell}
                      >{`$${line.totalAmount.toFixed(2)}`}</StyledTableCell>
                      <StyledTableCell
                        className={`${getAmountTextColor(
                          line.claimedAmount,
                          line.totalAmount,
                        )} ${classes.dollarAmountCell}`}
                      >{`$${line.claimedAmount.toFixed(2)}`}</StyledTableCell>
                      <StyledTableCell>
                        <IconButton
                          aria-label="edit line"
                          onClick={() => handleClickOpen(line)}
                        >
                          <Edit />
                        </IconButton>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <AddToScheduleDialog
        openDialog={openSchedule}
        handleCloseDialog={handleClose}
        jobMeasureQuoteInfo={selectedJobMeasureQuoteInfo}
        isNewItem={false}
        isUpdateMode={true}
      ></AddToScheduleDialog>
      <AddDuplicateDetailingScheduleDialog
        open={openDuplicateSchedule}
        fromScheduleId={group.schedule.toString()}
        fromScheduleName={group.area}
        onClose={handleDuplicateScheduleClose}
      ></AddDuplicateDetailingScheduleDialog>
      <EditDuplicateDetailingScheduleDialog
        open={openEditSchedule}
        fromScheduleId={group.schedule.toString()}
        fromScheduleName={group.area}
        onClose={handleEditScheduleClose}
      ></EditDuplicateDetailingScheduleDialog>
    </>
  );
}
